import { useMemo, useCallback, useRef } from 'react';
import { PageNode } from '@/types/sitemap';

interface Props {
  pages: PageNode[];
  selectedPageIds: string[];
  offset: { x: number; y: number };
  zoom: number;
  containerWidth: number;
  containerHeight: number;
  onNavigate: (offset: { x: number; y: number }) => void;
  className?: string;
}

const MINIMAP_WIDTH = 200;
const MINIMAP_HEIGHT = 140;
const NODE_WIDTH = 280;
const NODE_DEFAULT_HEIGHT = 120;
const PADDING = 40;

export function Minimap({ pages, selectedPageIds, offset, zoom, containerWidth, containerHeight, onNavigate, className }: Props) {
  const minimapRef = useRef<HTMLDivElement>(null);

  // Calculate bounds of all pages
  const bounds = useMemo(() => {
    if (pages.length === 0) return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pages) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + NODE_WIDTH);
      maxY = Math.max(maxY, p.y + NODE_DEFAULT_HEIGHT);
    }
    // Add padding
    return {
      minX: minX - PADDING,
      minY: minY - PADDING,
      maxX: maxX + PADDING,
      maxY: maxY + PADDING,
    };
  }, [pages]);

  const worldWidth = bounds.maxX - bounds.minX;
  const worldHeight = bounds.maxY - bounds.minY;

  // Scale to fit minimap
  const scale = Math.min(MINIMAP_WIDTH / worldWidth, MINIMAP_HEIGHT / worldHeight);

  // Viewport rect in canvas coordinates
  const viewX = (-offset.x / zoom);
  const viewY = (-offset.y / zoom);
  const viewW = containerWidth / zoom;
  const viewH = containerHeight / zoom;

  const handleClick = useCallback((e: React.MouseEvent) => {
    const rect = minimapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clickX = (e.clientX - rect.left) / scale + bounds.minX;
    const clickY = (e.clientY - rect.top) / scale + bounds.minY;
    // Center viewport on clicked point
    onNavigate({
      x: -(clickX - containerWidth / zoom / 2) * zoom,
      y: -(clickY - containerHeight / zoom / 2) * zoom,
    });
  }, [scale, bounds, containerWidth, containerHeight, zoom, onNavigate]);

  return (
    <div
      className={`relative bg-card/95 backdrop-blur rounded-xl node-shadow overflow-hidden cursor-pointer ${className || ''}`}
      style={{ height: MINIMAP_HEIGHT }}
      onClick={handleClick}
      ref={minimapRef}
    >
      {/* Page nodes */}
      {pages.map(page => {
        const x = (page.x - bounds.minX) * scale;
        const y = (page.y - bounds.minY) * scale;
        const w = NODE_WIDTH * scale;
        const h = NODE_DEFAULT_HEIGHT * scale;
        const isSelected = selectedPageIds.includes(page.id);
        return (
          <div
            key={page.id}
            className={`absolute rounded-[2px] ${isSelected ? 'ring-1 ring-foreground' : ''}`}
            style={{
              left: x,
              top: y,
              width: Math.max(w, 3),
              height: Math.max(h, 2),
              backgroundColor: page.color,
              opacity: isSelected ? 1 : 0.7,
            }}
          />
        );
      })}

      {/* Viewport indicator */}
      <div
        className="absolute border-2 border-primary/60 bg-primary/5 rounded-sm pointer-events-none"
        style={{
          left: (viewX - bounds.minX) * scale,
          top: (viewY - bounds.minY) * scale,
          width: viewW * scale,
          height: viewH * scale,
        }}
      />
    </div>
  );
}
